import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { SelectModule } from 'primeng/select';
import { TimelineModule } from 'primeng/timeline';
import { DialogModule } from 'primeng/dialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { DatePicker } from 'primeng/datepicker';
import { DeviceContextService } from '../device-context.service';
import { CloudApiService, FirmwareEntry } from '../../../services/cloud-api.service';
import { DeviceAction, DeviceActionStatus } from 'src/api/device-action';
import { SectionCardComponent } from '@/shared/ui/section-card/section-card.component';

interface FirmwareOption {
    id: string;
    version: string;
    name: string;
    fileName: string;
    fileSizeBytes: number;
    uploadedUtc: string;
    description?: string;
    isCurrent: boolean;
    isUpgrade: boolean;
}

@Component({
    selector: 'app-device-firmware',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        TagModule,
        SelectModule,
        TimelineModule,
        DialogModule,
        ProgressBarModule,
        MessageModule,
        SkeletonModule,
        TooltipModule,
        DatePicker,
        SectionCardComponent
    ],
    templateUrl: './device-firmware.html',
    styleUrl: './device-firmware.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeviceFirmware {
    private ctx = inject(DeviceContextService);
    private api = inject(CloudApiService);

    readonly loading = signal(true);
    readonly error = signal<string | null>(null);
    readonly firmwareList = signal<FirmwareOption[]>([]);
    readonly actions = signal<DeviceAction[]>([]);
    readonly actionsLoading = signal(false);

    // Update dialog
    readonly showUpdateDialog = signal(false);
    readonly selectedFirmware = signal<FirmwareOption | null>(null);
    readonly scheduling = signal(false);
    readonly scheduleDate = signal<Date | null>(null);
    readonly submitting = signal(false);
    readonly submitError = signal<string | null>(null);
    readonly minScheduleDate = new Date();

    private fetchToken = 0;

    readonly currentVersion = computed(() => {
        const device = this.ctx.device();
        return device?.payload?.firmwareVersion ?? device?.payload?.applianceInfo?.puf ?? null;
    });

    readonly deviceModel = computed(() => {
        const device = this.ctx.device();
        return device?.model ?? device?.payload?.applianceInfo?.modelName ?? null;
    });

    readonly availableUpdates = computed(() => {
        return this.firmwareList().filter(fw => fw.isUpgrade);
    });

    readonly latestAvailable = computed(() => {
        const upgrades = this.availableUpdates();
        return upgrades.length > 0 ? upgrades[0] : null;
    });

    readonly isUpToDate = computed(() => {
        return !this.loading() && this.availableUpdates().length === 0 && this.firmwareList().length > 0;
    });

    readonly recentActions = computed(() => {
        return this.actions()
            .filter(a => a.actionType === 'FirmwareUpdate')
            .slice(0, 5);
    });

    readonly hasPendingUpdate = computed(() => {
        return this.actions().some(a =>
            a.actionType === 'FirmwareUpdate' &&
            (a.status === 'Pending' || a.status === 'Dispatched')
        );
    });

    constructor() {
        effect(() => {
            const device = this.ctx.device();
            const model = device?.model;
            const deviceId = device?.id;

            if (!model || !deviceId) {
                this.firmwareList.set([]);
                this.loading.set(false);
                return;
            }

            const requestId = ++this.fetchToken;
            this.loading.set(true);
            this.error.set(null);

            this.loadFirmwareData(model, deviceId, requestId);
        });
    }

    private async loadFirmwareData(model: string, deviceId: string, requestId: number): Promise<void> {
        try {
            const [firmware, actions] = await Promise.all([
                this.api.getFirmwareList(model),
                this.api.getDeviceActions(deviceId, { limit: 20 })
            ]);

            if (requestId !== this.fetchToken) return;

            const currentVer = this.currentVersion();
            const options: FirmwareOption[] = firmware
                .map(fw => ({
                    id: fw.id,
                    version: fw.version,
                    name: fw.name,
                    fileName: fw.originalFileName,
                    fileSizeBytes: fw.fileSizeBytes,
                    uploadedUtc: fw.uploadedUtc,
                    description: fw.description,
                    isCurrent: currentVer ? this.compareVersions(fw.version, currentVer) === 0 : false,
                    isUpgrade: currentVer ? this.compareVersions(fw.version, currentVer) > 0 : true
                }))
                .filter(fw => !currentVer || this.compareVersions(fw.version, currentVer) >= 0)
                .sort((a, b) => this.compareVersions(b.version, a.version));

            this.firmwareList.set(options);
            this.actions.set(actions);
        } catch (err: any) {
            if (requestId !== this.fetchToken) return;
            this.error.set(err?.message ?? 'Failed to load firmware information');
        } finally {
            if (requestId === this.fetchToken) {
                this.loading.set(false);
            }
        }
    }

    openUpdateDialog(firmware: FirmwareOption): void {
        this.selectedFirmware.set(firmware);
        this.scheduling.set(false);
        this.scheduleDate.set(null);
        this.submitError.set(null);
        this.showUpdateDialog.set(true);
    }

    async confirmUpdate(): Promise<void> {
        const firmware = this.selectedFirmware();
        const device = this.ctx.device();
        if (!firmware || !device) return;

        this.submitting.set(true);
        this.submitError.set(null);

        try {
            // Get download URL
            const downloadInfo = await this.api.getFirmwareDownloadUrl(firmware.id);

            const schedule = this.scheduleDate();
            await this.api.createDeviceAction(device.id, {
                actionType: 'FirmwareUpdate',
                payload: {
                    firmwareId: firmware.id,
                    version: firmware.version,
                    fileName: downloadInfo.fileName,
                    downloadUrl: downloadInfo.downloadUrl
                },
                scheduledForUtc: schedule ? schedule.toISOString() : undefined
            });

            this.showUpdateDialog.set(false);

            // Refresh actions list
            const actions = await this.api.getDeviceActions(device.id, { limit: 20 });
            this.actions.set(actions);
        } catch (err: any) {
            this.submitError.set(err?.message ?? 'Failed to schedule firmware update');
        } finally {
            this.submitting.set(false);
        }
    }

    refresh(): void {
        const device = this.ctx.device();
        if (!device?.model || !device?.id) return;
        const requestId = ++this.fetchToken;
        this.loading.set(true);
        this.error.set(null);
        this.loadFirmwareData(device.model, device.id, requestId);
    }

    formatFileSize(bytes: number): string {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    formatDate(value?: string | null): string {
        if (!value) return '—';
        try {
            return new Date(value).toLocaleDateString(undefined, {
                year: 'numeric', month: 'short', day: 'numeric'
            });
        } catch {
            return value;
        }
    }

    formatDateTime(value?: string | null): string {
        if (!value) return '—';
        try {
            return new Date(value).toLocaleString();
        } catch {
            return value;
        }
    }

    statusSeverity(status: DeviceActionStatus): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
        switch (status) {
            case 'Succeeded': return 'success';
            case 'Pending': return 'info';
            case 'Dispatched': return 'warn';
            case 'Failed': return 'danger';
            case 'Cancelled': return 'secondary';
            default: return 'info';
        }
    }

    statusIcon(status: DeviceActionStatus): string {
        switch (status) {
            case 'Succeeded': return 'pi pi-check-circle';
            case 'Pending': return 'pi pi-clock';
            case 'Dispatched': return 'pi pi-send';
            case 'Failed': return 'pi pi-times-circle';
            case 'Cancelled': return 'pi pi-ban';
            default: return 'pi pi-circle';
        }
    }

    private compareVersions(a: string, b: string): number {
        // Only compare first 3 segments (major.minor.patch); ignore 4th build segment
        const partsA = a.split('.').slice(0, 3).map(Number);
        const partsB = b.split('.').slice(0, 3).map(Number);
        for (let i = 0; i < 3; i++) {
            const numA = partsA[i] || 0;
            const numB = partsB[i] || 0;
            if (numA !== numB) return numA - numB;
        }
        return 0;
    }
}
